import { apiClient, ensureCsrfCookie } from '@/api/client'
import type { ApiSuccessResponse } from '@/types/api'

export type WorkMode = 'onsite' | 'remote' | 'hybrid'
export type ProfileVisibility = 'public' | 'recruiters_only' | 'hidden'
export type EmploymentType = 'full_time' | 'part_time' | 'internship' | 'contract' | 'freelance'
export type SkillLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert'
export type LanguageProficiency = 'basic' | 'conversational' | 'fluent' | 'native'
export type SocialLinkType = 'linkedin' | 'github' | 'portfolio' | 'website' | 'other'

export interface StudentExperience {
  id: number
  companyName: string
  jobTitle: string
  employmentType: EmploymentType
  location: string | null
  startDate: string
  endDate: string | null
  isCurrent: boolean
  description: string | null
}

export interface StudentEducation {
  id: number
  institutionName: string
  degree: string
  course: string | null
  specialization: string | null
  boardOrUniversity: string | null
  startYear: number
  endYear: number | null
  percentageOrCgpa: string | null
  isCurrent: boolean
}

export interface StudentSkill {
  id: number
  skillName: string
  skillLevel: SkillLevel | null
  yearsOfExperience: number | null
  isPrimary: boolean
}

export interface StudentCertification {
  id: number
  name: string
  issuer: string | null
  issuedOn: string | null
  expiresOn: string | null
  credentialUrl: string | null
}

export interface StudentProject {
  id: number
  title: string
  description: string | null
  role: string | null
  startDate: string | null
  endDate: string | null
  projectUrl: string | null
}

export interface StudentLanguage {
  id: number
  languageName: string
  proficiencyLevel: LanguageProficiency
}

export interface StudentSocialLink {
  id: number
  type: SocialLinkType
  url: string
}

export interface StudentDocument {
  id: number
  documentType: 'marksheet' | 'certificate' | 'other'
  originalName: string
  mimeType: string
  fileSize: number
  createdAt: string
}

export interface StudentProfile {
  id: number
  headline: string | null
  careerSummary: string | null
  dob: string | null
  gender: string | null
  maritalStatus: string | null
  currentCity: string | null
  currentState: string | null
  currentCountry: string | null
  preferredWorkMode: WorkMode | null
  noticePeriodDays: number | null
  currentCtc: number | null
  expectedCtc: number | null
  salaryCurrency: string | null
  totalExperienceMonths: number | null
  relevantExperienceMonths: number | null
  isFresher: boolean
  hasResume: boolean
  hasProfilePhoto: boolean
  profileVisibility: ProfileVisibility
  profileCompletionPercent: number
  experiences: StudentExperience[]
  educations: StudentEducation[]
  skills: StudentSkill[]
  certifications: StudentCertification[]
  projects: StudentProject[]
  languages: StudentLanguage[]
  socialLinks: StudentSocialLink[]
  documents: StudentDocument[]
  fieldValues: Record<string, unknown>
  fieldVisibility: Record<string, boolean>
}

export interface ProfileFieldOption {
  value: string
  label: string
}

export interface ProfileFieldDefinition {
  key: string
  label: string
  inputType:
    'text' | 'textarea' | 'select' | 'multi_select' | 'number' | 'date' | 'boolean' | 'file'
  isRequired: boolean
  isMultiValue: boolean
  options: ProfileFieldOption[]
}

export interface ProfileFieldGroup {
  name: string
  slug: string
  fields: ProfileFieldDefinition[]
}

async function withCsrf<T>(fn: () => Promise<T>): Promise<T> {
  await ensureCsrfCookie()
  return fn()
}

function unwrap<T>(promise: Promise<{ data: ApiSuccessResponse<T> }>): Promise<T> {
  return promise.then(({ data }) => data.data)
}

export const profileApi = {
  get: () => unwrap<StudentProfile>(apiClient.get('/students/profile')),

  update: (payload: Record<string, unknown>) =>
    withCsrf(() => unwrap<StudentProfile>(apiClient.put('/students/profile', payload))),

  updateVisibility: (payload: Record<string, boolean>) =>
    withCsrf(() => unwrap<StudentProfile>(apiClient.put('/students/profile/visibility', payload))),

  fields: () => unwrap<ProfileFieldGroup[]>(apiClient.get('/students/profile/fields')),

  updateFields: (payload: Record<string, unknown>) =>
    withCsrf(() => unwrap<StudentProfile>(apiClient.put('/students/profile/fields', payload))),

  uploadResume: (file: File) =>
    withCsrf(() => {
      const form = new FormData()
      form.append('resume', file)
      return apiClient.post('/students/profile/resume', form)
    }),
  downloadResume: () =>
    unwrap<{ downloadUrl: string }>(apiClient.get('/students/profile/resume/download')),

  uploadPhoto: (file: File) =>
    withCsrf(() => {
      const form = new FormData()
      form.append('photo', file)
      return apiClient.post('/students/profile/photo', form)
    }),
  downloadPhoto: () =>
    unwrap<{ downloadUrl: string }>(apiClient.get('/students/profile/photo/download')),

  documents: {
    list: () => unwrap<StudentDocument[]>(apiClient.get('/students/profile/documents')),
    upload: (file: File, documentType: StudentDocument['documentType']) =>
      withCsrf(() => {
        const form = new FormData()
        form.append('document', file)
        form.append('document_type', documentType)
        return apiClient.post('/students/profile/documents', form)
      }),
    download: (id: number) =>
      unwrap<{ downloadUrl: string }>(apiClient.get(`/students/profile/documents/${id}/download`)),
    remove: (id: number) => withCsrf(() => apiClient.delete(`/students/profile/documents/${id}`)),
  },

  experiences: {
    create: (payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.post('/students/profile/experiences', payload)),
    update: (id: number, payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.put(`/students/profile/experiences/${id}`, payload)),
    remove: (id: number) => withCsrf(() => apiClient.delete(`/students/profile/experiences/${id}`)),
  },
  educations: {
    create: (payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.post('/students/profile/educations', payload)),
    update: (id: number, payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.put(`/students/profile/educations/${id}`, payload)),
    remove: (id: number) => withCsrf(() => apiClient.delete(`/students/profile/educations/${id}`)),
  },
  skills: {
    create: (payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.post('/students/profile/skills', payload)),
    update: (id: number, payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.put(`/students/profile/skills/${id}`, payload)),
    remove: (id: number) => withCsrf(() => apiClient.delete(`/students/profile/skills/${id}`)),
  },
  certifications: {
    create: (payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.post('/students/profile/certifications', payload)),
    update: (id: number, payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.put(`/students/profile/certifications/${id}`, payload)),
    remove: (id: number) =>
      withCsrf(() => apiClient.delete(`/students/profile/certifications/${id}`)),
  },
  projects: {
    create: (payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.post('/students/profile/projects', payload)),
    update: (id: number, payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.put(`/students/profile/projects/${id}`, payload)),
    remove: (id: number) => withCsrf(() => apiClient.delete(`/students/profile/projects/${id}`)),
  },
  languages: {
    create: (payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.post('/students/profile/languages', payload)),
    update: (id: number, payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.put(`/students/profile/languages/${id}`, payload)),
    remove: (id: number) => withCsrf(() => apiClient.delete(`/students/profile/languages/${id}`)),
  },
  socialLinks: {
    create: (payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.post('/students/profile/social-links', payload)),
    update: (id: number, payload: Record<string, unknown>) =>
      withCsrf(() => apiClient.put(`/students/profile/social-links/${id}`, payload)),
    remove: (id: number) =>
      withCsrf(() => apiClient.delete(`/students/profile/social-links/${id}`)),
  },
}
